<template>
  <div
    v-if="storeLoading"
    class="tw-block md:tw-grid tw-grid-cols-1 tw-gap-x-[28px] xl:tw-gap-x-[54px]"
  >
    <b-skeleton height="689px" />
  </div>
  <div v-else>
    <div
      class="tw-p-[38px] tw-py-0 md:tw-p-[70px] md:tw-pt-[44px] tw-rounded-[20px] tw-border-[1px] tw-border-transparent md:tw-border-[var(--my-primary)] tw-bg-transparent md:tw-bg-[var(--my-white)]"
    >
      <router-link
        to="/my-contacts"
        class="tw-flex tw-items-center tw-justify-center tw-w-[32px] md:tw-w-[40px] tw-h-[32px] md:tw-h-[40px] tw-ms-[-10px] tw-mb-[10px] md:tw-mb-[16px] tw-rounded-full tw-border-[1px] tw-border-transparent"
      >
        <icon-arrow-left />
      </router-link>
      <h1
        class="tw-flex tw-justify-between md:tw-justify-start md:tw-gap-[24px] tw-mb-[30px] md:tw-mb-[36px] tw-text-[25px] md:tw-text-[35px] tw-leading-[1.2] tw-font-black"
      >
        <template v-if="contact">{{ contact.first_name }}</template>
        <button
          :disabled="loadingEdit"
          @click="editContact"
          class="tw-flex tw-items-center tw-justify-center tw-w-[32px] md:tw-w-[40px] tw-h-[32px] md:tw-h-[40px] tw-rounded-full tw-border-[1px] tw-border-[var(--my-white-soft2)] tw-bg-[var(--my-white-soft2)]"
        >
          <b-spinner v-if="loadingEdit" size="sm" />
          <icon-edit v-else />
        </button>
        <button
          :disabled="loadingEdit"
          @click="deleteContactHandler"
          class="tw-flex tw-items-center tw-justify-center tw-w-[32px] md:tw-w-[40px] tw-h-[32px] md:tw-h-[40px] tw-rounded-full tw-border-[1px] tw-border-[var(--my-white-soft2)] tw-bg-[var(--my-white-soft2)] tw-ml-2"
          title="Delete Contact"
        >
          <b-spinner v-if="loadingEdit" size="sm" />
          <b-icon-trash class="tw-text-[var(--my-primary)]" width="26" height="26" v-else />
        </button>
      </h1>

      <contact-data v-if="contact" :contact="contact" />

      <div
        v-if="contact"
        class="tw-flex tw-justify-center tw-gap-x-[10px] tw-mt-[32px] md:tw-mt-[76px] tw-py-[28px] md:tw-py-[42px] tw-border-y tw-border-[rgba(0,0,0,0.1)]"
      >
        <contact-list-item-link
          :data="contact.phone"
          type="tel"
          class="tw-w-[34px] tw-h-[34px] md:tw-w-[44px] md:tw-h-[44px]"
        />
        <contact-list-item-link
          :data="contact.phone"
          type="sms"
          class="tw-w-[34px] tw-h-[34px] md:tw-w-[44px] md:tw-h-[44px]"
        />
        <contact-list-item-link
          :data="contact.email"
          type="email"
          class="tw-w-[34px] tw-h-[34px] md:tw-w-[44px] md:tw-h-[44px]"
        />
        <contact-list-item-link
          :data="contact.phone"
          type="whatsapp"
          class="tw-w-[34px] tw-h-[34px] md:tw-w-[44px] md:tw-h-[44px]"
        />
        <button
          class="tw-flex tw-items-center tw-justify-center tw-w-[34px] tw-h-[34px] md:tw-w-[44px] md:tw-h-[44px] tw-rounded-full tw-bg-[var(--my-white-soft)] tw-text-[var(--my-primary)]"
          @click="() => this.$root.$emit('bv::show::modal', 'my-social-share-popup')"
        >
          <icon-social-share class="md:tw-w-[26px] md:tw-h-[26px]" />
        </button>
      </div>

      <div v-if="contact" class="tw-max-w-[318px] tw-mt-[50px] tw-mx-auto">
        <p class="tw-mb-[20px] md:tw-text-center tw-text-[16px]">
          {{ $t("donor_dashboard.my_contacts_click_to_change_status", "Click to change status") }}
        </p>

        <div class="tw-mb-[20px] md:tw-mb-[24px]">
          <my-dropdown
            :value="contactStatusRef"
            variant="my-status-select"
            @set-status="setStatus"
          />
        </div>
        <my-btn class="tw-w-full" btnStyle="my-primary" @click="showAddAction">{{
          $t("donor_dashboard.my_contacts_btn_add_an_action", "Add An Action")
        }}</my-btn>
      </div>
    </div>

    <div
      class="tw-mt-[40px] md:tw-mt-[50px] tw-px-[38px] tw-py-[0px] md:tw-px-[70px] md:tw-py-[64px] tw-rounded-[20px] tw-border-[1px] tw-border-transparent md:tw-border-[var(--my-primary)] tw-text-[18px] tw-bg-transparent md:tw-bg-[var(--my-white)]"
    >
      <p class="tw-mb-[24px] tw-tracking-[0.01em]">
        {{ $t("donor_dashboard.my_contacts_notes_title", "Notes") }}
      </p>
      <div
        class="tw-p-[1em] tw-text-[18px] tw-h-[320px] tw-rounded-[20px] tw-bg-[var(--my-white-soft)]"
      >
        {{ contact ? contact.note : "" }}
      </div>

      <div v-if="actionsLoading" class="tw-flex tw-justify-center tw-my-[20px]">
        <b-skeleton height="578px" class="tw-w-full" />
      </div>
      <div v-else>
        <p class="tw-mt-[24px] tw-mb-[2px] tw-tracking-[0.01em]">
          {{ $t("donor_dashboard.my_contacts_actions_taken", "Actions Taken") }}
        </p>
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 md:tw-gap-x-[40px] xl:tw-gap-x-[80px]">
          <template v-if="actions.length === 0">
            <div class="tw-mb-[30px]">
              <p
                class="tw-mb-[7px] tw-text-right rtl:tw-text-left tw-text-[10px] tw-italic tw-tracking-[0.01em]"
              >
                &nbsp;
              </p>
              <div class="tw-p-[22px] tw-pt-[16px] tw-rounded-[20px] tw-bg-[var(--my-white-soft)]">
                <p class="tw-mb-[0] tw-italic tw-tracking-[0.01em]">
                  {{ $t("donor_dashboard.my_contacts_no_actions_found", "No actions found") }}
                </p>
              </div>
            </div>
          </template>
          <template v-else>
            <div v-for="action in actions" :key="action.id" class="tw-mb-[30px]">
              <p
                class="tw-mb-[7px] tw-text-right rtl:tw-text-left tw-text-[10px] tw-italic tw-tracking-[0.01em]"
              >
                {{ formatDate(action.created_at) }}
              </p>
              <div class="tw-p-[22px] tw-pt-[16px] tw-rounded-[20px] tw-bg-[var(--my-white-soft)]">
                <p class="tw-mb-[7px] tw-text-[var(--my-primary)] tw-tracking-[0.01em]">
                  {{ $t(ACTION_TYPES[action.type].nameKey, ACTION_TYPES[action.type].nameDefault) }}
                </p>
                <p v-if="action.note" class="tw-mb-[0] tw-italic tw-tracking-[0.01em]">
                  {{ action.note }}
                </p>
                <p v-if="action.follow_up_date" class="tw-mb-[0] tw-italic tw-tracking-[0.01em]">
                  {{ formatDate(action.follow_up_date) }}
                </p>
              </div>
            </div>
          </template>
        </div>
        <div
          v-if="actionsPagination.total > actionsPagination.per_page"
          class="tw-flex tw-justify-center tw-my-[20px]"
        >
          <MyPagination
            :value="actionsPagination.page"
            @input="onActionsPageChange"
            :total-rows="actionsPagination.total"
            :per-page="actionsPagination.per_page"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import ContactData from "@/views/donor-dashboard/my-contacts/contact/contact-data.vue";
import iconEdit from "@/views/donor-dashboard/my-contacts/icons/edit.vue";
import iconArrowLeft from "@/views/donor-dashboard/my-contacts/icons/arrow-left.vue";
import ContactListItemLink from "@/views/donor-dashboard/my-contacts/contact-list/contact-list-item/contact-list-item-link.vue";
import myBtn from "@/views/donor-dashboard/my-contacts/ui/my-btn.vue";
import myDropdown from "@/views/donor-dashboard/my-contacts/ui/my-dropdown.vue";
import { useTeamLeaderContacts } from "@/composables/useTeamLeaderContacts";
import { useTeamLeaderContactActions } from "@/composables/useTeamLeaderContactActions";
import { onMounted, computed, watch, ref } from "vue";
import { getCurrentInstance } from "vue";
import { notificationsMixin } from "@/mixins";
import { actions as ACTION_TYPES, STATUS_KEYS } from "../constants";
import { useProctectedMyTeamsList } from "../../compositions/my-teams";
import iconSocialShare from "@/views/donor-dashboard/my-contacts/icons/social-share.vue";
import MyPagination from "@/views/donor-dashboard/my-contacts/ui/my-pagination.vue";

const vm = getCurrentInstance();
const contactId = computed(() => vm.proxy.$route.params.id);
const {
  contact,
  fetchContactById,
  updateContactStatus,
  storeError,
  storeLoading,
  teams,
  deleteContact,
} = useTeamLeaderContacts();
const {
  actions,
  storeLoading: actionsLoading,
  storePagination: actionsPagination,
  fetchContactActions,
} = useTeamLeaderContactActions();
const notificationsMixinMakeErrorToast =
  notificationsMixin.methods.$_notificationsMixin_makeErrorToast.bind(vm.proxy);
const notificationsMixinHandleCatch =
  notificationsMixin.methods.$_notificationsMixin_handleCatch.bind(vm.proxy);
const { fetchProtectedMyTeams } = useProctectedMyTeamsList();
const router = vm.proxy.$router;

const contactStatusRef = ref({ [STATUS_KEYS.NEW_LEADS]: true });

function filterTeamsByCampaignId(teams, campaignId) {
  return teams.filter((team) => team.campaign.campaign_id === campaignId).map((team) => team.team);
}

const loadingEdit = ref(false);
onMounted(() => {
  loadingEdit.value = true;
  fetchContactById(contactId.value)
    .then(async (contact) => {
      const data = await fetchProtectedMyTeams({
        extend: ["stats", "campaign", "organization", "permissions"],
      });
      const teamsByCampaign = filterTeamsByCampaignId(data, contact.campaign_id);
      teams.value = teamsByCampaign;
      contactStatusRef.value = {
        [contact.status]: true,
      };
    })
    .then(() => {
      fetchContactActions(contactId.value);
    })
    .catch((err) => {
      notificationsMixinHandleCatch(err);
    })
    .finally(() => {
      loadingEdit.value = false;
    });
});

watch(storeError, () => {
  if (storeError.value) {
    notificationsMixinMakeErrorToast("Error", storeError.value);
  }
});

const setStatus = async (status) => {
  if (!contact.value) return;
  let statusKey = status;
  if (typeof status === "object" && status !== null) {
    statusKey = Object.keys(status).find((key) => status[key]);
  }
  await updateContactStatus(contact.value.id, { status: statusKey });
  contactStatusRef.value = {
    [statusKey]: true,
  };
  fetchContactById(contact.value.id);
};

const showAddAction = () => {
  vm.proxy.$root.$emit("bv::show::modal", "my-add-action-popup");
  vm.proxy.$root.$emit("my-add-action-popup-data", { data: { id: contactId.value } });
};

const editContact = () => {
  loadingEdit.value = true;
  fetchContactById(contactId.value)
    .then((data) => {
      vm.proxy.$root.$emit("bv::show::modal", "my-add-contact-popup");
      vm.proxy.$root.$emit("my-add-contact-popup-data", { edit: true, data });
    })
    .catch((err) => {
      notificationsMixinHandleCatch(err);
    })
    .finally(() => {
      loadingEdit.value = false;
    });
};

const deleteContactHandler = async () => {
  if (!contact.value) return;
  if (!window.confirm("Are you sure you want to delete this contact?")) return;
  loadingEdit.value = true;
  try {
    await deleteContact(contact.value.id);
    router.push("/my-contacts");
  } catch (err) {
    notificationsMixinMakeErrorToast("Error", err.message || "Failed to delete contact");
  } finally {
    loadingEdit.value = false;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString();
};

// Handle page change for actions pagination
function onActionsPageChange(page) {
  fetchContactActions(contactId.value, { page });
}
</script>
